// -----------------------------------------------------------------------------
// Client-side internal-chat store.
//
// The `/chat/*` endpoints don't exist yet (see
// `docs/pending-endpoints.md → Internal Chat`), so we persist the full chat
// state — users, channels, messages, read markers — in `localStorage` and
// broadcast changes via a `CustomEvent`. This mirrors the pattern used by
// `utils/jobLocks.ts` and `utils/dismantlingTimers.ts`.
//
// Semantics:
//   • Seed data is created on first load so the feature is immediately
//     usable — realistic channels, plausible colleagues and a few sample
//     messages so the UI never looks empty.
//   • Messages are appended; edits update an existing row's `body` and set
//     `editedAt`; reactions toggle per user per emoji.
//   • `markRead(channelId)` writes the user's current time to
//     `readState[channelId]`; unread counts are derived by comparing each
//     channel's messages against that timestamp.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  IChatChannel,
  IChatMessage,
  IChatReadState,
  IChatUser,
} from "../models/Chat";

const LOCAL_STORAGE_KEY = "wilma.chat.v1";
const CHANGE_EVENT = "wilma:chat:change";

interface ChatStore {
  users: IChatUser[];
  channels: IChatChannel[];
  messages: IChatMessage[];
  /** Per-user read state: `readState[userId][channelId] = lastReadAtIso`. */
  readState: Record<string, IChatReadState>;
}

// ---------------------------------------------------------------------------
// Storage plumbing
// ---------------------------------------------------------------------------

const emptyStore = (): ChatStore => ({
  users: [],
  channels: [],
  messages: [],
  readState: {},
});

const readStore = (): ChatStore => {
  if (typeof window === "undefined") return emptyStore();
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyStore();
    return {
      users: Array.isArray(parsed.users) ? parsed.users : [],
      channels: Array.isArray(parsed.channels) ? parsed.channels : [],
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      readState:
        parsed.readState && typeof parsed.readState === "object"
          ? parsed.readState
          : {},
    };
  } catch {
    return emptyStore();
  }
};

const writeStore = (next: ChatStore) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Quota / private-mode errors: silently swallow — the chat should
    // stay functional in-memory even if persistence fails.
  }
};

const randomId = () =>
  `cid-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;

// ---------------------------------------------------------------------------
// Seed data
// ---------------------------------------------------------------------------

const SEED_USERS: IChatUser[] = [
  { id: "u-jordan",  forename: "Jordan",  surname: "Miller",    role: "Sales Lead",     presence: "online" },
  { id: "u-priya",   forename: "Priya",   surname: "Shah",      role: "Sales",          presence: "online" },
  { id: "u-olivia",  forename: "Olivia",  surname: "Evans",     role: "Sales",          presence: "away" },
  { id: "u-ben",     forename: "Ben",     surname: "Carter",    role: "Parts",          presence: "online" },
  { id: "u-hanna",   forename: "Hanna",   surname: "Lindqvist", role: "Workshop",       presence: "offline" },
  { id: "u-marcus",  forename: "Marcus",  surname: "Johnson",   role: "Workshop Lead",  presence: "online" },
  { id: "u-yasmin",  forename: "Yasmin",  surname: "Ahmed",     role: "Parts / Listings", presence: "online" },
  { id: "u-kieran",  forename: "Kieran",  surname: "Doyle",     role: "Dismantler",     presence: "away" },
  { id: "u-lewis",   forename: "Lewis",   surname: "Thompson",  role: "Dismantler",     presence: "online" },
  { id: "u-ravi",    forename: "Ravi",    surname: "Patel",     role: "Dismantler",     presence: "offline" },
];

// Quick lookup helper for the seed message factory below.
const u = (id: string) => SEED_USERS.find((x) => x.id === id)!.id;

const minutesAgo = (min: number) =>
  new Date(Date.now() - min * 60_000).toISOString();

const SEED_CHANNELS: IChatChannel[] = [
  {
    id: "ch-general",
    kind: "channel",
    name: "general",
    description: "Company-wide announcements and watercooler chat.",
    memberIds: SEED_USERS.map((x) => x.id),
    createdAt: minutesAgo(60 * 24 * 30),
    lastMessageAt: minutesAgo(12),
  },
  {
    id: "ch-sales",
    kind: "channel",
    name: "sales",
    description: "Hot leads, buyer queries and conversions.",
    memberIds: [u("u-jordan"), u("u-priya"), u("u-olivia"), u("u-yasmin")],
    createdAt: minutesAgo(60 * 24 * 20),
    lastMessageAt: minutesAgo(4),
  },
  {
    id: "ch-workshop",
    kind: "channel",
    name: "workshop",
    description: "Job coordination, parts needed, daily blockers.",
    memberIds: [u("u-marcus"), u("u-hanna"), u("u-kieran"), u("u-lewis"), u("u-ravi")],
    createdAt: minutesAgo(60 * 24 * 20),
    lastMessageAt: minutesAgo(28),
  },
  {
    id: "ch-listings",
    kind: "channel",
    name: "parts-listings",
    description: "New listings, pricing questions, eBay issues.",
    memberIds: [u("u-ben"), u("u-yasmin"), u("u-priya"), u("u-marcus")],
    createdAt: minutesAgo(60 * 24 * 18),
    lastMessageAt: minutesAgo(75),
  },
  {
    id: "ch-random",
    kind: "channel",
    name: "random",
    description: "Non-work chat — memes, lunch runs, weekend plans.",
    memberIds: SEED_USERS.map((x) => x.id),
    createdAt: minutesAgo(60 * 24 * 25),
    lastMessageAt: minutesAgo(140),
  },
];

const seedMessages = (): IChatMessage[] => [
  // #general
  mkMsg("ch-general", "u-jordan",
    "Morning all — power is back on in the front office. Billing server finished rebooting around 7:30.",
    minutesAgo(60 * 8)),
  mkMsg("ch-general", "u-marcus",
    "Nice — workshop was already up on the backup circuit, so nothing lost.",
    minutesAgo(60 * 8 - 3)),
  mkMsg("ch-general", "u-olivia",
    "Stock take summary is pinned in #parts-listings. Please pick up any variance flags this week.",
    minutesAgo(60 * 3)),
  mkMsg("ch-general", "u-hanna",
    "Anyone free to help carry the new racking panels from reception? 🏋️",
    minutesAgo(40)),
  mkMsg("ch-general", "u-ben",
    "On my way.",
    minutesAgo(12),
    { reactions: [{ emoji: "👍", userIds: ["u-hanna", "u-marcus"] }] }),

  // #sales
  mkMsg("ch-sales", "u-jordan",
    "Big enquiry just in from Westmead — they want the full front end off the Kuga in bay A1.",
    minutesAgo(180)),
  mkMsg("ch-sales", "u-priya",
    "I'll take it — chasing a couple of photos then I'll quote.",
    minutesAgo(175)),
  mkMsg("ch-sales", "u-olivia",
    "Heads up: the BMW 320d buyer from yesterday paid this morning. Need the gearbox on a pallet by 3pm.",
    minutesAgo(90)),
  mkMsg("ch-sales", "u-priya",
    "Quoted Westmead £620 + VAT on the front end. Deposit landed.",
    minutesAgo(4),
    { reactions: [{ emoji: "🎉", userIds: ["u-jordan", "u-yasmin"] }] }),

  // #workshop
  mkMsg("ch-workshop", "u-marcus",
    "Lift 2 is booked for the Q5 at 10:30 — please leave it clear.",
    minutesAgo(300)),
  mkMsg("ch-workshop", "u-kieran",
    "Started on the Passat gearbox. Two bolts seized — going to soak them and come back after lunch.",
    minutesAgo(240)),
  mkMsg("ch-workshop", "u-lewis",
    "Heads up: the airbag modules from the A-Class still need logging, can't list until they're binned.",
    minutesAgo(60)),
  mkMsg("ch-workshop", "u-hanna",
    "Onto it after I drop these panels in reception. 🔧",
    minutesAgo(28)),

  // #parts-listings
  mkMsg("ch-listings", "u-ben",
    "Just pushed 18 new listings — mostly Kuga and Golf. Prices are at the AI suggested midpoint.",
    minutesAgo(90 * 60 / 60)),
  mkMsg("ch-listings", "u-yasmin",
    "Nice — I'll watch the Kuga bumper, that's been selling in under 48h lately.",
    minutesAgo(82)),
  mkMsg("ch-listings", "u-priya",
    "eBay flagged two of the wheels for 'unverified VRM' again. Opening a ticket.",
    minutesAgo(75)),

  // #random
  mkMsg("ch-random", "u-marcus",
    "Who wants in on the chippy run?",
    minutesAgo(200)),
  mkMsg("ch-random", "u-kieran",
    "Chicken curry and chips for me please, ta.",
    minutesAgo(195),
    { reactions: [{ emoji: "🍛", userIds: ["u-marcus"] }] }),
  mkMsg("ch-random", "u-olivia",
    "Scampi and mushy peas 🙏",
    minutesAgo(193)),
  mkMsg("ch-random", "u-ben",
    "Meeting moved to 3, just a heads up.",
    minutesAgo(140)),
];

function mkMsg(
  channelId: string,
  authorId: string,
  body: string,
  createdAt: string,
  extras: Partial<IChatMessage> = {}
): IChatMessage {
  return {
    id: randomId(),
    channelId,
    authorId,
    body,
    createdAt,
    reactions: [],
    ...extras,
  };
}

/**
 * First-time bootstrap. Only runs if the store is empty — never clobbers
 * existing messages. The current user (from `useAuth`) is merged in lazily
 * in `useChatStore` since this module has no access to React context.
 */
function ensureSeeded(store: ChatStore): ChatStore {
  if (store.channels.length > 0) return store;
  return {
    ...store,
    users: SEED_USERS,
    channels: SEED_CHANNELS,
    messages: seedMessages(),
    readState: {},
  };
}

// ---------------------------------------------------------------------------
// Public mutation API
// ---------------------------------------------------------------------------

/** Merges the logged-in user into the store on first load. Runs often; safe
 * no-op once the user is already present with the same details. */
function upsertMe(
  store: ChatStore,
  me: { id: string; forename: string; surname: string; role?: string }
): ChatStore {
  const existing = store.users.find((x) => x.id === me.id);
  const meUser: IChatUser = {
    id: me.id,
    forename: me.forename,
    surname: me.surname,
    role: me.role ?? existing?.role ?? "Me",
    presence: "online",
    isMe: true,
  };
  const users = existing
    ? store.users.map((x) => (x.id === me.id ? meUser : { ...x, isMe: false }))
    : [meUser, ...store.users.map((x) => ({ ...x, isMe: false }))];

  // Auto-join every team channel so the seeded rooms are visible. DMs are
  // created on-demand, so we deliberately skip them here.
  const channels = store.channels.map((ch) => {
    if (ch.kind !== "channel") return ch;
    if (ch.memberIds.includes(me.id)) return ch;
    return { ...ch, memberIds: [...ch.memberIds, me.id] };
  });

  return { ...store, users, channels };
}

function sendMessageInternal(
  store: ChatStore,
  channelId: string,
  authorId: string,
  body: string,
  replyToId?: string
): ChatStore {
  const trimmed = body.trim();
  if (!trimmed) return store;
  const now = new Date().toISOString();
  const message: IChatMessage = {
    id: randomId(),
    channelId,
    authorId,
    body: trimmed,
    createdAt: now,
    reactions: [],
    replyToId,
  };
  const messages = [...store.messages, message];
  const channels = store.channels.map((c) =>
    c.id === channelId ? { ...c, lastMessageAt: now } : c
  );
  // Mark the sender as having read their own message — keeps their unread
  // badge from flashing on after they send.
  const nextReadForAuthor = {
    ...(store.readState[authorId] ?? {}),
    [channelId]: now,
  };
  const readState = {
    ...store.readState,
    [authorId]: nextReadForAuthor,
  };
  return { ...store, messages, channels, readState };
}

function editMessageInternal(
  store: ChatStore,
  messageId: string,
  body: string
): ChatStore {
  const trimmed = body.trim();
  if (!trimmed) return store;
  const messages = store.messages.map((m) =>
    m.id === messageId
      ? { ...m, body: trimmed, editedAt: new Date().toISOString() }
      : m
  );
  return { ...store, messages };
}

function toggleReactionInternal(
  store: ChatStore,
  messageId: string,
  emoji: string,
  userId: string
): ChatStore {
  const messages = store.messages.map((m) => {
    if (m.id !== messageId) return m;
    const existing = m.reactions.find((r) => r.emoji === emoji);
    if (!existing) {
      return {
        ...m,
        reactions: [...m.reactions, { emoji, userIds: [userId] }],
      };
    }
    const hasMine = existing.userIds.includes(userId);
    const nextUserIds = hasMine
      ? existing.userIds.filter((id) => id !== userId)
      : [...existing.userIds, userId];
    const nextReactions = nextUserIds.length
      ? m.reactions.map((r) =>
          r.emoji === emoji ? { ...r, userIds: nextUserIds } : r
        )
      : m.reactions.filter((r) => r.emoji !== emoji);
    return { ...m, reactions: nextReactions };
  });
  return { ...store, messages };
}

function ensureDmChannelInternal(
  store: ChatStore,
  userIdA: string,
  userIdB: string
): { store: ChatStore; channelId: string } {
  const existing = store.channels.find(
    (c) =>
      c.kind === "dm" &&
      c.memberIds.length === 2 &&
      c.memberIds.includes(userIdA) &&
      c.memberIds.includes(userIdB)
  );
  if (existing) return { store, channelId: existing.id };
  const now = new Date().toISOString();
  const channel: IChatChannel = {
    id: `dm-${randomId()}`,
    kind: "dm",
    name: "",
    memberIds: [userIdA, userIdB],
    createdAt: now,
    lastMessageAt: undefined,
  };
  return {
    store: { ...store, channels: [...store.channels, channel] },
    channelId: channel.id,
  };
}

function markReadInternal(
  store: ChatStore,
  channelId: string,
  userId: string
): ChatStore {
  const now = new Date().toISOString();
  const nextForUser = {
    ...(store.readState[userId] ?? {}),
    [channelId]: now,
  };
  return {
    ...store,
    readState: { ...store.readState, [userId]: nextForUser },
  };
}

function createChannelInternal(
  store: ChatStore,
  input: { name: string; description?: string; memberIds: string[] }
): { store: ChatStore; channel: IChatChannel } {
  const clean = input.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const now = new Date().toISOString();
  const channel: IChatChannel = {
    id: `ch-${randomId()}`,
    kind: "channel",
    name: clean || "channel",
    description: input.description?.trim() || undefined,
    memberIds: Array.from(new Set(input.memberIds)),
    createdAt: now,
  };
  return {
    store: { ...store, channels: [...store.channels, channel] },
    channel,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface CurrentUser {
  id: string;
  forename: string;
  surname: string;
  role?: string;
}

interface UseChatStoreResult {
  users: IChatUser[];
  channels: IChatChannel[];
  messages: IChatMessage[];
  /** Current logged-in chat user (synthetic record merged from `useAuth`). */
  me: IChatUser | null;
  /** Messages for a given channel, chronologically. */
  messagesFor: (channelId: string) => IChatMessage[];
  /** Number of messages newer than the user's lastReadAt for the channel. */
  unreadCount: (channelId: string) => number;
  send: (channelId: string, body: string, replyToId?: string) => void;
  editMessage: (messageId: string, body: string) => void;
  toggleReaction: (messageId: string, emoji: string) => void;
  startDm: (otherUserId: string) => string;
  markRead: (channelId: string) => void;
  createChannel: (input: {
    name: string;
    description?: string;
    memberIds: string[];
  }) => IChatChannel;
  userById: (id: string) => IChatUser | undefined;
}

export function useChatStore(currentUser: CurrentUser | null): UseChatStoreResult {
  const [store, setStore] = useState<ChatStore>(() => {
    const seeded = ensureSeeded(readStore());
    // Persist seed back so subsequent reads are a no-op.
    if (seeded !== readStore()) writeStore(seeded);
    return seeded;
  });

  // Merge the logged-in user whenever their identity changes.
  useEffect(() => {
    if (!currentUser) return;
    setStore((prev) => {
      const next = upsertMe(prev, currentUser);
      if (next !== prev) writeStore(next);
      return next;
    });
  }, [currentUser?.id, currentUser?.forename, currentUser?.surname, currentUser?.role]);

  // Cross-tab / same-tab sync — re-read storage whenever the change event
  // fires (another tab posted a message, or we wrote from a helper).
  useEffect(() => {
    const refresh = () => {
      setStore(readStore());
    };
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const me = useMemo(
    () => store.users.find((u) => u.isMe) ?? null,
    [store.users]
  );

  const userById = useCallback(
    (id: string) => store.users.find((u) => u.id === id),
    [store.users]
  );

  const messagesFor = useCallback(
    (channelId: string) =>
      store.messages
        .filter((m) => m.channelId === channelId)
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)),
    [store.messages]
  );

  const unreadCount = useCallback(
    (channelId: string) => {
      if (!me) return 0;
      const lastRead = store.readState[me.id]?.[channelId];
      if (!lastRead) {
        // Unseen channel: everything not authored by me counts as unread,
        // but cap at 99 for display.
        return store.messages
          .filter((m) => m.channelId === channelId && m.authorId !== me.id)
          .length;
      }
      const cutoff = Date.parse(lastRead);
      return store.messages.filter(
        (m) =>
          m.channelId === channelId &&
          m.authorId !== me.id &&
          Date.parse(m.createdAt) > cutoff
      ).length;
    },
    [store.messages, store.readState, me]
  );

  const commit = useCallback((next: ChatStore) => {
    writeStore(next);
    setStore(next);
  }, []);

  const send = useCallback(
    (channelId: string, body: string, replyToId?: string) => {
      if (!me) return;
      commit(sendMessageInternal(readStore(), channelId, me.id, body, replyToId));
    },
    [commit, me]
  );

  const editMessage = useCallback(
    (messageId: string, body: string) => {
      commit(editMessageInternal(readStore(), messageId, body));
    },
    [commit]
  );

  const toggleReaction = useCallback(
    (messageId: string, emoji: string) => {
      if (!me) return;
      commit(toggleReactionInternal(readStore(), messageId, emoji, me.id));
    },
    [commit, me]
  );

  const startDm = useCallback(
    (otherUserId: string): string => {
      if (!me) return "";
      const { store: next, channelId } = ensureDmChannelInternal(
        readStore(),
        me.id,
        otherUserId
      );
      commit(next);
      return channelId;
    },
    [commit, me]
  );

  const markRead = useCallback(
    (channelId: string) => {
      if (!me) return;
      commit(markReadInternal(readStore(), channelId, me.id));
    },
    [commit, me]
  );

  const createChannel = useCallback(
    (input: {
      name: string;
      description?: string;
      memberIds: string[];
    }): IChatChannel => {
      const base = readStore();
      const withMe = me
        ? {
            ...input,
            memberIds: Array.from(new Set([...input.memberIds, me.id])),
          }
        : input;
      const { store: next, channel } = createChannelInternal(base, withMe);
      commit(next);
      return channel;
    },
    [commit, me]
  );

  return {
    users: store.users,
    channels: store.channels,
    messages: store.messages,
    me,
    messagesFor,
    unreadCount,
    send,
    editMessage,
    toggleReaction,
    startDm,
    markRead,
    createChannel,
    userById,
  };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function initialsFor(user: Pick<IChatUser, "forename" | "surname">): string {
  return `${user.forename?.[0] ?? ""}${user.surname?.[0] ?? ""}`.toUpperCase();
}

export function fullNameFor(user: Pick<IChatUser, "forename" | "surname">): string {
  return `${user.forename ?? ""} ${user.surname ?? ""}`.trim();
}

/** Label for a channel — `#general` for named rooms, the other member's
 * name for DMs. */
export function channelLabel(
  channel: IChatChannel,
  meId: string | null,
  usersById: Map<string, IChatUser>
): string {
  if (channel.kind === "channel") return `#${channel.name}`;
  const otherId = channel.memberIds.find((id) => id !== meId);
  if (!otherId) return "Direct message";
  const other = usersById.get(otherId);
  return other ? fullNameFor(other) : "Direct message";
}
